import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MapPin, Star, Check, X as XIcon, Clock, Eye, Pencil, Trash, ChatCircle, ShieldCheck, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ status }) {
  const colors = {
    pending: "bg-[#E8A341]/10 text-[#E8A341]", confirmed: "bg-[#3B7D5A]/10 text-[#3B7D5A]",
    active: "bg-[#2C5545]/10 text-[#2C5545]", completed: "bg-[#5C665D]/10 text-[#5C665D]",
    cancelled: "bg-[#D94A4A]/10 text-[#D94A4A]", rejected: "bg-[#D94A4A]/10 text-[#D94A4A]",
    paid: "bg-[#3B7D5A]/10 text-[#3B7D5A]", unpaid: "bg-[#E8A341]/10 text-[#E8A341]",
    initiated: "bg-[#E8A341]/10 text-[#E8A341]",
  };
  return <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${colors[status] || "bg-gray-100 text-gray-600"}`}>{status}</span>;
}

function BookingCard({ booking, isOwner, onAction, onDeposit }) {
  const img = booking.item_image ? `${API_URL}/api/files/${booking.item_image}` : null;
  const hasDeposit = booking.deposit_amount > 0;
  const depositStatus = booking.deposit_status;
  return (
    <div className="bg-white border border-[#EAE7E0] rounded-2xl p-5 transition-all hover:shadow-sm" data-testid={`booking-${booking.id}`}>
      <div className="flex gap-4">
        {img && <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#F5F3EE] flex-shrink-0"><img src={img} alt="" className="w-full h-full object-cover" /></div>}
        <div className="flex-1 min-w-0">
          <Link to={`/item/${booking.item_id}`} className="text-sm font-medium text-[#1A1C1A] hover:text-[#2C5545] line-clamp-1">{booking.item_title}</Link>
          <p className="text-xs text-[#5C665D] mt-0.5">{booking.start_date} to {booking.end_date} ({booking.days} days)</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <StatusBadge status={booking.status} />
            <StatusBadge status={booking.payment_status} />
            {hasDeposit && depositStatus && <StatusBadge status={depositStatus} />}
          </div>
        </div>
        <p className="text-sm font-semibold text-[#2C5545]">${booking.total_price?.toFixed(2)}</p>
      </div>
      {/* Deposit info */}
      {hasDeposit && (
        <div className="mt-3 pt-2 border-t border-[#EAE7E0]/50 text-xs text-[#5C665D] flex items-center gap-1">
          <ShieldCheck size={12} weight="duotone" /> Deposit: ${booking.deposit_amount?.toFixed(2)}
          {depositStatus === "refunded" && <span className="text-[#3B7D5A] font-medium ml-1">(Refunded)</span>}
          {depositStatus === "held_damage" && <span className="text-[#D94A4A] font-medium ml-1">(Held - Damage Reported)</span>}
          {depositStatus === "disputed" && <span className="text-[#E8A341] font-medium ml-1">(Disputed)</span>}
          {depositStatus === "refund_pending" && <span className="text-[#E8A341] font-medium ml-1">(Refund Pending)</span>}
        </div>
      )}
      {isOwner && booking.status === "pending" && (
        <div className="flex gap-2 mt-4 pt-3 border-t border-[#EAE7E0]">
          <button onClick={() => onAction(booking.id, "confirmed")} className="flex-1 flex items-center justify-center gap-1 bg-[#2C5545] text-white rounded-full py-2 text-xs font-medium hover:bg-[#224235] transition-all" data-testid={`confirm-booking-${booking.id}`}>
            <Check size={14} /> Confirm
          </button>
          <button onClick={() => onAction(booking.id, "rejected")} className="flex-1 flex items-center justify-center gap-1 bg-white text-[#D94A4A] border border-[#D94A4A]/30 rounded-full py-2 text-xs font-medium hover:bg-red-50 transition-all" data-testid={`reject-booking-${booking.id}`}>
            <XIcon size={14} /> Reject
          </button>
        </div>
      )}
      {isOwner && (booking.status === "confirmed" || booking.status === "active") && !depositStatus && (
        <div className="mt-4 pt-3 border-t border-[#EAE7E0]">
          <p className="text-xs text-[#5C665D] mb-2 font-medium">Item returned?</p>
          <div className="flex gap-2">
            <button onClick={() => onDeposit(booking.id, "good")} className="flex-1 flex items-center justify-center gap-1 bg-[#3B7D5A] text-white rounded-full py-2 text-xs font-medium hover:bg-[#2C6545] transition-all" data-testid={`return-good-${booking.id}`}>
              <Check size={14} /> Good Condition
            </button>
            <button onClick={() => onDeposit(booking.id, "damaged")} className="flex-1 flex items-center justify-center gap-1 bg-white text-[#D94A4A] border border-[#D94A4A]/30 rounded-full py-2 text-xs font-medium hover:bg-red-50 transition-all" data-testid={`return-damaged-${booking.id}`}>
              <WarningCircle size={14} /> Report Damage
            </button>
          </div>
        </div>
      )}
      {!isOwner && depositStatus === "held_damage" && (
        <div className="mt-4 pt-3 border-t border-[#EAE7E0]">
          <button onClick={() => onDeposit(booking.id, "dispute")} className="w-full flex items-center justify-center gap-1 bg-[#E8A341] text-white rounded-full py-2 text-xs font-medium hover:bg-[#D49230] transition-all" data-testid={`dispute-${booking.id}`}>
            <WarningCircle size={14} /> Dispute Damage Claim
          </button>
        </div>
      )}
      {!isOwner && booking.status === "pending" && booking.payment_status === "unpaid" && (
        <div className="mt-4 pt-3 border-t border-[#EAE7E0]">
          <button onClick={() => handlePayment(booking)} className="w-full flex items-center justify-center gap-1 bg-[#D96C4A] text-white rounded-full py-2 text-xs font-medium hover:bg-[#C25939] transition-all" data-testid={`pay-booking-${booking.id}`}>
            Pay Now
          </button>
        </div>
      )}
    </div>
  );
}

async function handlePayment(booking) {
  try {
    const { data } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin });
    if (data.url) window.location.href = data.url;
  } catch (err) {
    toast.error(err.response?.data?.detail || "Payment failed");
  }
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [myItems, setMyItems] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileForm, setProfileForm] = useState({ name: "", bio: "", location: "", phone: "" });
  const [reviewDialog, setReviewDialog] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });

  useEffect(() => {
    if (!user) return;
    setProfileForm({ name: user.name || "", bio: user.bio || "", location: user.location || "", phone: user.phone || "" });
    Promise.all([
      api.get("/items/my").then(r => setMyItems(r.data || [])),
      api.get("/bookings?role=renter").then(r => setMyBookings(r.data || [])),
      api.get("/bookings?role=owner").then(r => setRequests(r.data || [])),
    ]).finally(() => setLoading(false));
  }, [user]);

  const handleBookingAction = async (bookingId, status) => {
    try {
      await api.put(`/bookings/${bookingId}`, { status });
      toast.success(`Booking ${status}`);
      setRequests(prev => prev.map(b => b.id === bookingId ? { ...b, status } : b));
      setMyBookings(prev => prev.map(b => b.id === bookingId ? { ...b, status } : b));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const handleDeposit = async (bookingId, action) => {
    try {
      if (action === "good") {
        const { data } = await api.post(`/bookings/${bookingId}/return`, { condition: "good" });
        toast.success("Item returned! Deposit refund initiated.");
        setRequests(prev => prev.map(b => b.id === bookingId ? data : b));
        setMyBookings(prev => prev.map(b => b.id === bookingId ? data : b));
      } else if (action === "damaged") {
        const { data } = await api.post(`/bookings/${bookingId}/return`, { condition: "damaged", notes: "Owner reported damage" });
        toast.success("Damage reported. Deposit held.");
        setRequests(prev => prev.map(b => b.id === bookingId ? data : b));
        setMyBookings(prev => prev.map(b => b.id === bookingId ? data : b));
      } else if (action === "dispute") {
        const { data } = await api.post(`/bookings/${bookingId}/dispute`, { reason: "Renter disputes damage claim" });
        toast.success("Dispute filed.");
        setMyBookings(prev => prev.map(b => b.id === bookingId ? data : b));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await api.delete(`/items/${itemId}`);
      setMyItems(prev => prev.filter(i => i.id !== itemId));
      toast.success("Item deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleProfileSave = async () => {
    try {
      await api.put("/users/profile", profileForm);
      toast.success("Profile updated");
    } catch {
      toast.error("Update failed");
    }
  };

  const handleReview = async () => {
    if (!reviewDialog) return;
    try {
      await api.post("/reviews", { booking_id: reviewDialog.id, ...reviewForm });
      toast.success("Review submitted!");
      setReviewDialog(null);
      setReviewForm({ rating: 5, comment: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Review failed");
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#2C5545] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-[#FDFBF7] py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-medium text-[#1A1C1A] tracking-tight mb-8" data-testid="dashboard-heading">Dashboard</h1>

        <Tabs defaultValue="listings" className="space-y-6">
          <TabsList className="bg-white border border-[#EAE7E0] rounded-full p-1 h-auto flex-wrap">
            <TabsTrigger value="listings" className="rounded-full text-sm data-[state=active]:bg-[#2C5545] data-[state=active]:text-white px-4 py-2" data-testid="tab-listings">My Listings</TabsTrigger>
            <TabsTrigger value="rentals" className="rounded-full text-sm data-[state=active]:bg-[#2C5545] data-[state=active]:text-white px-4 py-2" data-testid="tab-rentals">My Rentals</TabsTrigger>
            <TabsTrigger value="requests" className="rounded-full text-sm data-[state=active]:bg-[#2C5545] data-[state=active]:text-white px-4 py-2 relative" data-testid="tab-requests">
              Requests
              {requests.filter(r => r.status === "pending").length > 0 && (
                <span className="ml-1.5 w-5 h-5 bg-[#D96C4A] text-white rounded-full text-[10px] inline-flex items-center justify-center">{requests.filter(r => r.status === "pending").length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="profile" className="rounded-full text-sm data-[state=active]:bg-[#2C5545] data-[state=active]:text-white px-4 py-2" data-testid="tab-profile">Profile</TabsTrigger>
          </TabsList>

          {/* My Listings */}
          <TabsContent value="listings">
            {myItems.length === 0 ? (
              <div className="text-center py-16 bg-white border border-[#EAE7E0] rounded-2xl">
                <p className="font-heading text-xl text-[#1A1C1A] mb-2">No listings yet</p>
                <p className="text-sm text-[#5C665D] mb-4">Start earning by listing your items.</p>
                <Link to="/create-listing" className="bg-[#2C5545] text-white rounded-full px-6 py-2.5 text-sm font-medium hover:bg-[#224235] transition-all inline-block" data-testid="create-first-listing">List Your First Item</Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {myItems.map(item => (
                  <div key={item.id} className="bg-white border border-[#EAE7E0] rounded-2xl overflow-hidden" data-testid={`my-item-${item.id}`}>
                    <div className="aspect-[16/10] bg-[#F5F3EE]">
                      {item.images?.[0] && <img src={`${API_URL}/api/files/${item.images[0]}`} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="font-heading text-lg font-medium text-[#1A1C1A] line-clamp-1">{item.title}</h3>
                        <StatusBadge status={item.status} />
                      </div>
                      <p className="text-sm text-[#2C5545] font-semibold mb-3">${item.price_per_day?.toFixed(2)}/day</p>
                      <div className="flex items-center gap-3 text-xs text-[#5C665D]">
                        <span className="flex items-center gap-1"><Eye size={12} /> {item.view_count || 0}</span>
                      </div>
                      <div className="flex gap-2 mt-3 pt-3 border-t border-[#EAE7E0]">
                        <Link to={`/item/${item.id}`} className="flex-1 flex items-center justify-center gap-1 bg-[#FDFBF7] border border-[#EAE7E0] rounded-full py-2 text-xs font-medium hover:border-[#2C5545] transition-all" data-testid={`view-item-${item.id}`}>
                          <Eye size={12} /> View
                        </Link>
                        <Link to={`/edit-listing/${item.id}`} className="flex-1 flex items-center justify-center gap-1 bg-[#FDFBF7] border border-[#EAE7E0] rounded-full py-2 text-xs font-medium hover:border-[#2C5545] transition-all" data-testid={`edit-item-${item.id}`}>
                          <Pencil size={12} /> Edit
                        </Link>
                        <button onClick={() => handleDeleteItem(item.id)} className="flex items-center justify-center gap-1 bg-[#FDFBF7] border border-[#EAE7E0] rounded-full px-3 py-2 text-xs text-[#D94A4A] hover:border-[#D94A4A] transition-all" data-testid={`delete-item-${item.id}`}>
                          <Trash size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My Rentals */}
          <TabsContent value="rentals">
            {myBookings.length === 0 ? (
              <div className="text-center py-16 bg-white border border-[#EAE7E0] rounded-2xl">
                <p className="font-heading text-xl text-[#1A1C1A] mb-2">No rentals yet</p>
                <p className="text-sm text-[#5C665D]">Browse items to start renting.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {myBookings.map(b => (
                  <div key={b.id}>
                    <BookingCard booking={b} isOwner={false} onAction={handleBookingAction} onDeposit={handleDeposit} />
                    {b.status === "completed" && (
                      <button onClick={() => setReviewDialog(b)} className="mt-2 text-xs text-[#D96C4A] font-medium hover:text-[#C25939]" data-testid={`review-booking-${b.id}`}>
                        <Star size={12} weight="fill" className="inline mr-1" /> Leave a Review
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Requests */}
          <TabsContent value="requests">
            {requests.length === 0 ? (
              <div className="text-center py-16 bg-white border border-[#EAE7E0] rounded-2xl">
                <p className="font-heading text-xl text-[#1A1C1A] mb-2">No booking requests</p>
                <p className="text-sm text-[#5C665D]">Requests from renters will appear here.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {requests.map(b => <BookingCard key={b.id} booking={b} isOwner={true} onAction={handleBookingAction} onDeposit={handleDeposit} />)}
              </div>
            )}
          </TabsContent>

          {/* Profile */}
          <TabsContent value="profile">
            <div className="bg-white border border-[#EAE7E0] rounded-2xl p-6 sm:p-8 max-w-lg space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Name</label>
                <input value={profileForm.name} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))} className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all" data-testid="profile-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Bio</label>
                <textarea value={profileForm.bio} onChange={e => setProfileForm(p => ({ ...p, bio: e.target.value }))} rows={3} className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all resize-none" data-testid="profile-bio-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Location</label>
                  <input value={profileForm.location} onChange={e => setProfileForm(p => ({ ...p, location: e.target.value }))} className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all" data-testid="profile-location-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Phone</label>
                  <input value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all" data-testid="profile-phone-input" />
                </div>
              </div>
              <button onClick={handleProfileSave} className="bg-[#2C5545] text-white hover:bg-[#224235] rounded-full px-6 py-2.5 text-sm font-medium transition-all hover:shadow-md hover:-translate-y-0.5" data-testid="profile-save-btn">Save Profile</button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Review Dialog */}
      <Dialog open={!!reviewDialog} onOpenChange={() => setReviewDialog(null)}>
        <DialogContent className="bg-white border-[#EAE7E0] rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1A1C1A]">Leave a Review</DialogTitle>
            <DialogDescription className="text-sm text-[#5C665D]">Share your experience.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-[#1A1C1A] mb-2">Rating</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(i => (
                  <button key={i} type="button" onClick={() => setReviewForm(p => ({ ...p, rating: i }))} data-testid={`review-star-${i}`}>
                    <Star size={24} weight={i <= reviewForm.rating ? "fill" : "regular"} className={i <= reviewForm.rating ? "text-[#E8A341]" : "text-[#EAE7E0]"} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Comment</label>
              <textarea value={reviewForm.comment} onChange={e => setReviewForm(p => ({ ...p, comment: e.target.value }))} rows={3} className="w-full bg-[#FDFBF7] border border-[#EAE7E0] rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#2C5545]/20 focus:border-[#2C5545] transition-all resize-none" data-testid="review-comment-input" />
            </div>
            <button onClick={handleReview} className="w-full bg-[#2C5545] text-white hover:bg-[#224235] rounded-full py-3 text-sm font-medium transition-all" data-testid="review-submit-btn">Submit Review</button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
